import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { Heart, Play, Clock, ArrowLeft, Users } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { slugify } from '../../utils/slug';
import { routeBuilders } from '../routes';

type Episode = {
  id: string;
  title: string;
  slug?: string | null;
  duration_seconds?: number | null;
  episode_number?: number | null;
  status?: string | null;
  cover_image_url?: string | null;
  description?: string | null;
};

type SeriesRecord = {
  id: string;
  title: string;
  slug?: string | null;
  description?: string | null;
  cover_image_url?: string | null;
  release_schedule?: string | null;
  status?: string | null;
  creator?: {
    id: string;
    display_name: string | null;
    avatar_url?: string | null;
    bio?: string | null;
  } | null;
  episodes?: Episode[];
};

type StoryShape = {
  id: string;
  title: string;
  slug?: string | null;
  author: string;
  creatorSlug?: string | null;
  creatorName?: string | null;
  coverImage: string;
  duration: string;
  totalChapters: number;
  genreIds: string[];
  description?: string | null;
  releaseSchedule?: string | null;
};

type SeriesLandingProps = {
  session: Session | null;
  onLikeStory: (storyId: string) => Promise<void> | void;
  onPlayStory: (story: StoryShape, options?: { startEpisodeId?: string }) => void;
  likedStories: Set<string>;
};

const FALLBACK_COVER =
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=800&q=80';
const IMAGES_BUCKET = 'Images';
const DEMO_USER_ID = import.meta.env.VITE_SUPABASE_DEMO_USER_ID as string | undefined;

const extractImageStoragePath = (raw?: string | null): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('http')) {
    return trimmed.replace(/^\/+/, '');
  }
  const bucketSegment = `/${IMAGES_BUCKET}/`;
  const bucketIndex = trimmed.indexOf(bucketSegment);
  if (bucketIndex === -1) return null;
  const withoutParams = trimmed.slice(bucketIndex + bucketSegment.length).split('?')[0];
  return decodeURIComponent(withoutParams);
};

const getDisplayImageUrl = async (raw?: string | null): Promise<string | null> => {
  if (!raw) return null;
  const storagePath = extractImageStoragePath(raw);
  if (!storagePath) return raw;
  const { data, error } = await supabase.storage
    .from(IMAGES_BUCKET)
    .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
  if (error || !data?.signedUrl) {
    console.error('Unable to generate signed URL for image', error);
    return raw;
  }
  return data.signedUrl;
};

const formatEpisodeDuration = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const formatTotalDuration = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return 'Listen now';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  }
  const secs = Math.floor(seconds % 60);
  return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
};

export function SeriesLanding({ session, onLikeStory, likedStories, onPlayStory }: SeriesLandingProps) {
  const { seriesSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [series, setSeries] = useState<SeriesRecord | null>(null);
  const [seriesStory, setSeriesStory] = useState<StoryShape | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [creatorAvatar, setCreatorAvatar] = useState<string>(FALLBACK_COVER);
  const [seriesCover, setSeriesCover] = useState<string>(FALLBACK_COVER);
  const [seriesLikes, setSeriesLikes] = useState(0);
  const [creatorLikes, setCreatorLikes] = useState(0);
  const [creatorFollowers, setCreatorFollowers] = useState(0);
  const isLiked = useMemo(() => (series ? likedStories.has(series.id) : false), [likedStories, series]);

  const countFollowers = useCallback(async (creatorId: string): Promise<number> => {
    const { count, error } = await supabase
      .from('creator_follows')
      .select('follower_id', { count: 'exact', head: true })
      .eq('creator_id', creatorId);
    if (error) {
      const message = (error as any)?.message || '';
      const isMissingTable = message.includes('does not exist') || error.code === '42P01';
      if (!isMissingTable) {
        console.warn('Unable to count followers from creator_follows', error);
      }
      return 0;
    }
    return count ?? 0;
  }, []);

  const countCreatorLikes = useCallback(async (creatorId: string): Promise<number> => {
    const { data, error } = await supabase
      .from('series')
      .select('id')
      .eq('creator_id', creatorId)
      .eq('status', 'published');
    if (error) {
      console.error('Unable to load creator series for likes', error);
      return 0;
    }
    const seriesIds = (data ?? []).map((item) => item.id).filter(Boolean);
    if (!seriesIds.length) return 0;
    const { count, error: likeError } = await supabase
      .from('likes')
      .select('id', { count: 'exact', head: true })
      .in('series_id', seriesIds);
    if (likeError) {
      console.error('Unable to count likes for creator', likeError);
      return 0;
    }
    return count ?? 0;
  }, []);

  const loadSeries = useCallback(async () => {
    if (!seriesSlug) {
      setError('Series not found.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const normalizedSlug = decodeURIComponent(seriesSlug);
      const selectClause = `
          id,
          title,
          slug,
          description,
          cover_image_url,
          release_schedule,
          status,
          creator_id,
          creator:profiles!series_creator_id_fkey (
            id,
            display_name,
            avatar_url,
            bio
          ),
          episodes (
            id,
            title,
            slug,
            duration_seconds,
            episode_number,
            status,
            cover_image_url,
            description
          )
        `;

      const { data, error: seriesError } = await supabase
        .from('series')
        .select(selectClause)
        .eq('slug', normalizedSlug)
        .maybeSingle();

      let resolvedSeries = data;
      if ((!resolvedSeries || seriesError) && normalizedSlug) {
        const searchPattern = `%${normalizedSlug.replace(/-/g, '%')}%`;
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('series')
          .select(selectClause)
          .ilike('title', searchPattern)
          .limit(1);
        if (!fallbackError && (fallbackData?.length ?? 0) > 0) {
          resolvedSeries = fallbackData[0] as SeriesRecord;
        } else if (seriesError) {
          throw seriesError;
        } else if (fallbackError) {
          throw fallbackError;
        }
      }

      if (!resolvedSeries) {
        throw seriesError || new Error('Series not found');
      }

      const cover = (await getDisplayImageUrl(resolvedSeries.cover_image_url)) ?? FALLBACK_COVER;
      const avatar =
        (await getDisplayImageUrl(resolvedSeries.creator?.avatar_url ?? null)) ?? FALLBACK_COVER;
      const publishedEpisodes =
        (resolvedSeries.episodes ?? [])
          .filter((ep) => (ep.status ?? '').toLowerCase() === 'published')
          .sort((a, b) => (a.episode_number ?? 0) - (b.episode_number ?? 0)) ?? [];
      const totalDuration = (publishedEpisodes ?? []).reduce(
        (sum, ep) => sum + (ep.duration_seconds ?? 0),
        0,
      );
      const story: StoryShape = {
        id: resolvedSeries.id,
        title: resolvedSeries.title,
        slug: resolvedSeries.slug ?? slugify(resolvedSeries.title || resolvedSeries.id || 'series'),
        author: resolvedSeries.creator?.display_name ?? 'Unknown Creator',
        creatorSlug: resolvedSeries.creator?.display_name
          ? slugify(resolvedSeries.creator.display_name)
          : null,
        creatorName: resolvedSeries.creator?.display_name ?? 'Unknown Creator',
        coverImage: cover,
        duration: formatTotalDuration(totalDuration),
        totalChapters: publishedEpisodes.length,
        genreIds: [],
        description: resolvedSeries.description,
        releaseSchedule: resolvedSeries.release_schedule,
      };

      const [
        { count: likesCount = 0, error: likeError } = { count: 0, error: null },
        followerCount,
        creatorLikeCount,
      ] = await Promise.all([
        supabase.from('likes').select('id', { count: 'exact', head: true }).eq('series_id', resolvedSeries.id),
        resolvedSeries.creator?.id ? countFollowers(resolvedSeries.creator.id) : Promise.resolve(0),
        resolvedSeries.creator?.id ? countCreatorLikes(resolvedSeries.creator.id) : Promise.resolve(0),
      ]);
      if (likeError) {
        console.error('Unable to count likes for series', likeError);
      }

      setSeries(resolvedSeries);
      setSeriesStory(story);
      setEpisodes(publishedEpisodes);
      setSeriesCover(cover);
      setCreatorAvatar(avatar);
      setSeriesLikes(likesCount ?? 0);
      setCreatorFollowers(followerCount ?? 0);
      setCreatorLikes(creatorLikeCount ?? 0);
    } catch (err) {
      console.error('Unable to load series landing', err);
      setError('Unable to load this series right now. Please try again soon.');
    } finally {
      setLoading(false);
    }
  }, [countCreatorLikes, countFollowers, seriesSlug]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  const handleLike = async () => {
    if (!series) return;
    try {
      await onLikeStory(series.id);
      const canPersistLike = Boolean(session?.user?.id || DEMO_USER_ID);
      if (!canPersistLike) return;
      setSeriesLikes((prev) => (isLiked ? Math.max(0, prev - 1) : prev + 1));
    } catch (err) {
      console.error('Unable to toggle like from series landing', err);
    }
  };

  const handlePlayEpisode = (episodeId?: string) => {
    if (!seriesStory || !episodeId) return;
    onPlayStory(seriesStory, { startEpisodeId: episodeId });
  };

  if (loading) {
    return (
      <div className="page-shell flex items-center justify-center min-h-[60vh]">
        <p className="text-base-content/60">Loading series…</p>
      </div>
    );
  }

  if (error || !series) {
    return (
      <div className="page-shell flex flex-col items-center justify-center min-h-[60vh] text-center gap-4">
        <p className="text-base-content/70">{error ?? 'Series not found.'}</p>
        <button
          onClick={() => navigate(routeBuilders.home())}
          className="btn btn-outline btn-primary"
        >
          Go home
        </button>
      </div>
    );
  }

  return (
    <div className="page-shell space-y-8">
      <button
        onClick={() => navigate(-1)}
        className="btn btn-ghost btn-primary btn-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="grid gap-6 md:grid-cols-[260px,1fr] items-start">
        <div className="rounded-3xl overflow-hidden bg-base-200 shadow-sm w-full max-w-[250px] aspect-square mx-auto md:mx-0">
          <img src={seriesCover} alt={series.title} className="w-full h-full object-cover" />
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-xs uppercase text-base-content/60 tracking-wide">
              {series.release_schedule || 'New series'}
            </p>
            <h1 className="text-4xl font-bold leading-tight">{series.title}</h1>
          </div>
          <p className="text-base-content/70 leading-relaxed whitespace-pre-line">{series.description}</p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleLike}
              className={`btn btn-sm ${isLiked ? 'btn-primary' : 'btn-outline btn-primary'}`}
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
              {isLiked ? 'Saved' : 'Save'}
              <span className="text-sm text-base-content/60">{seriesLikes.toLocaleString()} likes</span>
            </button>
            {episodes.length > 0 && seriesStory && (
              <button
                onClick={() => handlePlayEpisode(episodes[0]?.id)}
                className="btn btn-primary btn-sm"
              >
                <Play className="w-4 h-4" />
                Play first episode
              </button>
            )}
          </div>
        </div>
      </div>

      <section className="rounded-3xl border border-base-200 bg-base-100 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Episodes</h2>
          {seriesStory && episodes.length > 0 && (
            <button
              onClick={() => handlePlayEpisode(episodes[0]?.id)}
              className="btn btn-ghost btn-primary btn-sm"
            >
              <Play className="w-4 h-4" />
              Play from start
            </button>
          )}
        </div>
        {episodes.length === 0 ? (
          <p className="text-sm text-base-content/60">No published episodes yet.</p>
        ) : (
          <div className="divide-y divide-base-200">
            {episodes.map((episode, index) => (
              <div key={episode.id} className="flex items-center gap-4 py-4">
                <div className="w-12 h-12 rounded-xl bg-base-200 flex items-center justify-center text-base-content/60 font-semibold">
                  #{episode.episode_number ?? index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-base-content truncate">{episode.title}</p>
                  <p className="text-sm text-base-content/60 line-clamp-2">
                    {episode.description || 'No description'}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-sm text-base-content/70">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatEpisodeDuration(episode.duration_seconds)}
                  </span>
                  {seriesStory && (
                    <button
                      onClick={() => handlePlayEpisode(episode.id)}
                      className="btn btn-outline btn-primary btn-xs"
                    >
                      Play
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {series.creator && (
        <section className="rounded-3xl border border-base-200 bg-base-100 p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-base-200">
              <img src={creatorAvatar} alt={series.creator.display_name ?? 'Creator'} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">{series.creator.display_name ?? 'Creator'}</h3>
                <Link
                  to={routeBuilders.creatorPublic(slugify(series.creator.display_name || series.creator.id))}
                  className="text-sm text-base-content/60 hover:text-base-content"
                >
                  View profile
                </Link>
              </div>
              <p className="text-sm text-base-content/70 line-clamp-3 mt-1">{series.creator.bio || 'No bio yet.'}</p>
              <div className="flex items-center gap-4 text-sm text-base-content/80 mt-3">
                <span className="inline-flex items-center gap-1">
                  <Heart className="w-4 h-4" />
                  {creatorLikes.toLocaleString()} likes received
                </span>
                <span className="inline-flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {creatorFollowers.toLocaleString()} followers
                </span>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
